import { Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import Button, { type ButtonType } from "./Button"
import cn from "../../../utils/classes"
import Icon from "./Icon"

export interface ModalProps {
  text: {
    title: string
    cancelButton?: string
    submitButton?: string
    secondaryButton?: string
    dialogAriaLabel?: string
  }
  isOpen: boolean
  children?: React.ReactNode
  onSubmit?: () => void
  submitLoading?: boolean
  onClose: () => void
  onCancel?: () => void
  onSecondary?: () => void
  secondaryLoading?: boolean
  onCancelButtonFocus?: () => void
  treatOutsideClickAsClose?: boolean
  panelStyleOverrides?: string
  submitButtonType?: ButtonType
  cancelButtonType?: ButtonType
  secondaryButtonType?: ButtonType
  disableSubmit?: boolean
  justifyButtons?: string
  cancelButtonRootStyleOverrides?: string
  cancelButtonStyleOverrides?: string
  submitButtonRootStyleOverrides?: string
  submitButtonStyleOverrides?: string
  secondaryButtonRootStyleOverrides?: string
  secondaryButtonStyleOverrides?: string
  titleStyleOverrides?: string
  omitCloseIcon?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  text,
  isOpen = false,
  children,
  onSubmit,
  submitLoading = false,
  onClose,
  onCancel,
  onSecondary,
  secondaryLoading = false,
  onCancelButtonFocus,
  treatOutsideClickAsClose = false,
  panelStyleOverrides,
  submitButtonType = "primary",
  cancelButtonType = "white",
  secondaryButtonType = "white",
  disableSubmit = false,
  justifyButtons = "justify-between",
  cancelButtonRootStyleOverrides,
  cancelButtonStyleOverrides,
  submitButtonRootStyleOverrides,
  submitButtonStyleOverrides,
  secondaryButtonRootStyleOverrides,
  secondaryButtonStyleOverrides,
  titleStyleOverrides,
  omitCloseIcon = false,
}) => {
  const panelStyle = cn(
    "relative transform rounded-lg bg-white pl-[24px] pr-[14px] pt-[14px] pb-[24px] text-left shadow-xl transition-all sm:my-8 sm:max-w-lg",
    panelStyleOverrides,
  )
  const divTitleStyle = cn(
    "inline-flex justify-between items-center w-full",
    titleStyleOverrides,
  )

  const titleStyle = cn(
    "text-base font-semibold leading-6 text-gray-900 m-0",
    titleStyleOverrides,
  )

  const modalId = `modal-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
  const ignoreOutsideClick: (() => void) | undefined = treatOutsideClickAsClose
    ? onClose
    : () => {}

  // Accessibility: if no visible title is provided, set an aria-label on the dialog
  // so assistive tech still has an accessible name. We avoid rendering an empty
  // heading element which triggers empty-heading a11y errors.
  const dialogAriaLabel = text.dialogAriaLabel || text.title

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        onClose={ignoreOutsideClick}
        id={modalId}
        role="dialog"
        aria-label={dialogAriaLabel}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-35 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={panelStyle}>
                <div className="flex flex-row justify-space-around gap-[4px]">
                  <div className="w-full">
                    <div className="w-full sm:flex flex-col sm:items-start">
                      <div className={divTitleStyle}>
                        {text?.title ? (
                          <Dialog.Title as="h3" className={titleStyle}>
                            {text.title}
                          </Dialog.Title>
                        ) : null}
                      </div>
                      <div className="w-full">{children}</div>
                    </div>
                    {(!!onCancel || !!onSubmit) && (
                      <div
                        className={`mt-[28px] flex flex-row ${justifyButtons}`}
                      >
                        {!!onCancel && (
                          <Button
                            disabled={!!submitLoading || !!secondaryLoading}
                            type={cancelButtonType}
                            onClick={onCancel}
                            buttonLabel={text.cancelButton || "Cancel"}
                            roundedFull={false}
                            styleOverrides={cancelButtonStyleOverrides}
                            rootStyleOverrides={cancelButtonRootStyleOverrides}
                            onFocus={onCancelButtonFocus}
                          />
                        )}
                        {!!onSecondary && (
                          <Button
                            disabled={!!submitLoading}
                            type={secondaryButtonType}
                            onClick={onSecondary}
                            loading={secondaryLoading}
                            buttonLabel={text.secondaryButton || "Secondary"}
                            roundedFull={false}
                            styleOverrides={secondaryButtonStyleOverrides}
                            rootStyleOverrides={
                              secondaryButtonRootStyleOverrides
                            }
                          />
                        )}
                        {!!onSubmit && (
                          <Button
                            type={submitButtonType}
                            onClick={onSubmit}
                            buttonLabel={text.submitButton || "Submit"}
                            loading={submitLoading}
                            roundedFull={false}
                            disabled={disableSubmit || secondaryLoading}
                            rootStyleOverrides={submitButtonRootStyleOverrides}
                            styleOverrides={submitButtonStyleOverrides}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {!omitCloseIcon && (
                    <div className="float-right">
                      <Button
                        disabled={submitLoading || secondaryLoading}
                        styleOverrides={`${submitLoading || secondaryLoading ? "cursor-not-allowed" : "cursor-pointer"} py-0 border-0 rounded-md bg-white minimum-touchtarget top-0 right-0 px-0`}
                        onClick={onClose}
                        roundedFull={false}
                        type="transparent"
                        buttonLabel={
                          <>
                            <span className="sr-only">Close</span>
                            <Icon iconType="x" styleOverrides="w-4 h-4" />
                          </>
                        }
                      />
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
